import css from './Wrapper.module.scss'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { PerformerActions } from 'store/pages/performer/actions'

import { BalloonBgPosition, BalloonSize, BalloonTipPosition, ComponentColor } from 'meta/backendControlled/structure'
import { Breakpoints } from 'meta/layout/breakpoints'
import { PerformerType } from 'meta/pages/performer'

import Menu, { MenuSkeleton } from 'components/BackendControlled/Component/Menu'
import { GridColumn, GridContainer } from 'components/Layout/Grid'
import CollectionBalloon from 'components/Pages/Collection/Balloon/CollectionBalloon'
import CollectionDetails from 'components/Pages/Collection/CollectionDetails'
import CollectionDetailsSkeleton from 'components/Pages/Collection/CollectionDetails/CollectionDetailsSkeleton'
import InfoBox from 'components/Pages/Collection/InfoBox'
import PerformerHead from 'components/Pages/Performer/PerformerHead'
import { useCurrentLanguage } from 'hooks/useCurrentLanguage'
import { usePerformerActiveEvents, usePerformerActiveTypeticketcollections, usePerformerDetails } from 'hooks/usePerformer'

import { getPerformersMenuItems, isPerformerArtist } from './utils'
import { useAppDispatch } from 'store'
import { MAX_BULK_REQUEST_SIZE } from 'meta/pages/v4/bulk'
import { chunkArrayInGroups } from 'utils/array'
import EventDataFetcher from 'components/Pages/Collection/BulkDataFetcher/EventDataFetcher'
import TypeticketcollectionDataFetcher from 'components/Pages/Collection/BulkDataFetcher/TypeticketcollectionDataFetcher'
import SeasonList from 'components/Pages/Collection/SeasonList'
import EventList from 'components/Pages/Collection/EventList'
import EventListSkeleton from 'components/Pages/Collection/EventList/EventListSkeleton'
import TypeTicketCollectionToCollectionBridge from 'components/Pages/Collection/BulkDataFetcher/TypeTicketCollectionToCollectionBridge'

interface Props {
  id: string
}

const Wrapper: React.FC<Props> = (props) => {
  const { id } = props

  const { t } = useTranslation()

  const language = useCurrentLanguage()

  const details = usePerformerDetails({ id, language })
  const activeEvents = usePerformerActiveEvents({ id, language })
  const activeTypeticketcollections = usePerformerActiveTypeticketcollections({ id, language })
  
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (activeEvents && activeEvents.metadata && activeEvents.metadata.hasMorePages) {
      dispatch(
        PerformerActions.getActiveEvents({
          code: id,
          page: activeEvents.metadata.page + 1,
        })
      )
    }
  }, [activeEvents])

  useEffect(() => {
    if (
      activeTypeticketcollections &&
      activeTypeticketcollections.metadata &&
      activeTypeticketcollections.metadata.hasMorePages
    ) {
      dispatch(
        PerformerActions.getActiveTypeticketcollections({
          code: id,
          page: activeTypeticketcollections.metadata.page + 1,
        })
      )
    }
  }, [activeTypeticketcollections])

  useEffect(() => { 
    if (details && details.code) {
      dispatch(PerformerActions.fetchMetadata({ code: details.code }))
    }
  }, [details])
  
  return (
    <div className={css.collectionWrapper}>
      {details && <PerformerHead id={id} />}
      <GridContainer>
        <GridColumn gridColumnStart={1} gridColumnEnd={{ [Breakpoints.tablet]: 17, [Breakpoints.mobile]: 17 }}>
          <CollectionBalloon
            className={css.balloonWrapper}
            backgroundUrl={details && details.assets.find((x) => x.format == 'BACKGROUND')?.url}
            color={ComponentColor.black}
            size={BalloonSize.medium}
            backgroundPosition={BalloonBgPosition.center}
            tipPosition={BalloonTipPosition.right}
            containsMenu
          >
            {details && activeEvents && activeTypeticketcollections && (
              <>
                <CollectionDetails
                  //fbPixel={details.fbPixel}
                  name={details.name}
                  id={id}
                  cover={{
                    imgUrl: details.assets?.find((x) => x.format == 'SQUARE')?.url,
                    name: details.name,
                    type: details.type.key,
                  }}
                  header={{
                    title: details.name,
                    type: details.type.key,
                    tags: details.tags.map(x => { return { tag: `#${x.name}` }}),
                    //location: details.location,
                  }}
                />
                {
                  <Menu
                    underlineFirst
                    insideBalloon
                    isSticky
                    list={getPerformersMenuItems(details, activeEvents, activeTypeticketcollections, t)}
                  />
                }
              </>
            )}

            {!details && (
              <>
                <CollectionDetailsSkeleton />
                <MenuSkeleton list={[]} />
              </>
            )}
          </CollectionBalloon>
          {details && (
            <>
              {activeTypeticketcollections && activeTypeticketcollections.data && (
                <SeasonList
                  anchorId={'id_abbonamenti'}
                  collectionId={id}
                  typeticketCollectionIds={Object.values(activeTypeticketcollections.data).map(x => x.items.map(y => y.id)).flat()}
                  isPerformer
                />
              )}
              {activeEvents && activeEvents.data && (
                <EventList
                  anchorId={'id_eventi'}
                  eventCodes={Object.values(activeEvents.data).map(x => x.items.map(y => y.code)).flat()}
                  isPerformer
                />
              )}
            </>
          )}
          {!details && <EventListSkeleton />}
        </GridColumn>
      </GridContainer>
      {details && (
        <InfoBox
          galleryData={details.assets?.filter((x) => x.format == 'GALLERY')}
          descData={{
            name: details.name,
            description: details.bio,
            social: details.externalUris,
            isArtist: isPerformerArtist(details.type.key.toUpperCase() as PerformerType),
          }}
          anchorId={'id_info'}
          bgImgUrl={details.assets?.filter((x) => x.format == 'GALLERY')[0]?.url}
          isPerformer
        />
      )}

      {activeEvents &&
        Object.values(activeEvents.data)?.map((el) => {
          const codes = el.items.map(x => x.code)
          return chunkArrayInGroups(codes, MAX_BULK_REQUEST_SIZE).map((codes, key) => (
            <EventDataFetcher key={key} eventIds={codes} />
          ))
        })}

      {activeTypeticketcollections &&
        Object.values(activeTypeticketcollections.data)?.map((el) => {
          const ids = el.items.map(x => x.id)
          return chunkArrayInGroups(ids, MAX_BULK_REQUEST_SIZE).map((ids, key) => (
            <TypeticketcollectionDataFetcher key={key} typeticketIds={ids} />
          ))
        })}

      {activeTypeticketcollections && activeTypeticketcollections.data &&
        Object.values(activeTypeticketcollections.data)?.map((el, key) => {
        const ids = el.items.map(x => x.id)
        return <TypeTicketCollectionToCollectionBridge key={key} typeticketIds={ids} />
      })}

    </div>
  )
}

export default Wrapper
